import React, { useState, useEffect } from "react"
import { FolderPlus, Trash2, Edit3, UploadCloud, Image, Images, Plus, X, ChevronLeft, Save, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import * as settingsService from "@/services/settingsService"
import type { GalleryAlbum, GalleryImage } from "@/services/settingsService"

export function GalleryManager() {
    const [albums, setAlbums] = useState<GalleryAlbum[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [activeAlbumId, setActiveAlbumId] = useState<string | null>(null)
    const [newAlbumTitle, setNewAlbumTitle] = useState("")
    const [editingAlbumId, setEditingAlbumId] = useState<string | null>(null)
    const [editingTitle, setEditingTitle] = useState("")
    const [uploading, setUploading] = useState(false)

    // Load gallery on mount
    useEffect(() => {
        const fetchGallery = async () => {
            try {
                const data = await settingsService.getGallery()
                setAlbums(data)
            } catch (err: any) {
                toast.error("Lỗi khi tải thư viện ảnh: " + err.message)
            } finally {
                setLoading(false)
            }
        }
        fetchGallery()
    }, [])

    const handleSave = async (updatedAlbums: GalleryAlbum[]) => {
        setSaving(true)
        try {
            await settingsService.updateGallery(updatedAlbums)
            setAlbums(updatedAlbums)
            toast.success("Đã lưu thay đổi vào cơ sở dữ liệu")
        } catch (err: any) {
            toast.error("Không thể lưu cấu hình: " + err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleCreateAlbum = () => {
        if (!newAlbumTitle.trim()) {
            toast.error("Vui lòng nhập tên Album")
            return
        }

        const newAlbum: GalleryAlbum = {
            id: `album-${Date.now()}`,
            title: newAlbumTitle.trim(),
            cover: "images/generated-1773763950437.png", // fallback cover
            images: [],
        }

        const updated = [...albums, newAlbum]
        setAlbums(updated)
        setNewAlbumTitle("")
        handleSave(updated)
    }

    const handleStartRename = (album: GalleryAlbum, e: React.MouseEvent) => {
        e.stopPropagation()
        setEditingAlbumId(album.id)
        setEditingTitle(album.title)
    }

    const handleSaveRename = (albumId: string) => {
        if (!editingTitle.trim()) {
            toast.error("Tên Album không được để trống")
            return
        }

        const updated = albums.map((al) => {
            if (al.id === albumId) {
                return { ...al, title: editingTitle.trim() }
            }
            return al
        })
        setAlbums(updated)
        setEditingAlbumId(null)
        handleSave(updated)
    }

    const handleDeleteAlbum = async (album: GalleryAlbum, e: React.MouseEvent) => {
        e.stopPropagation()
        if (!confirm(`Bạn có chắc chắn muốn xóa album "${album.title}"? Tất cả ảnh bên trong sẽ bị xóa khỏi máy chủ.`)) {
            return
        }

        // 1. Delete all images of this album from the disk
        try {
            for (const img of album.images) {
                await settingsService.deleteGalleryFile(img.url).catch(() => {})
            }
        } catch (err) {
            console.error("Lỗi khi dọn dẹp file:", err)
        }

        // 2. Remove album from state & save
        const updated = albums.filter((al) => al.id !== album.id)
        setAlbums(updated)
        if (activeAlbumId === album.id) {
            setActiveAlbumId(null)
        }
        handleSave(updated)
    }

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, albumId: string) => {
        const fileList = e.target.files
        if (!fileList || fileList.length === 0) return

        const files = Array.from(fileList)
        setUploading(true)
        toast.info(`Đang tải lên ${files.length} ảnh...`)

        try {
            // Upload to server
            const urls = await settingsService.uploadGalleryImages(files)

            // Add new images to active album
            const newImages: GalleryImage[] = urls.map((url, i) => ({
                id: `img-${Date.now()}-${i}`,
                url,
                uploadedAt: new Date().toISOString(),
            }))

            const updated = albums.map((al) => {
                if (al.id === albumId) {
                    const mergedImages = [...al.images, ...newImages]
                    // If cover is default placeholder, set first uploaded image as cover
                    const cover = al.cover.includes("images/generated") && mergedImages.length > 0
                        ? mergedImages[0].url
                        : al.cover
                    return {
                        ...al,
                        cover,
                        images: mergedImages,
                    }
                }
                return al
            })

            setAlbums(updated)
            handleSave(updated)
            toast.success(`Đã tải lên thành công ${files.length} ảnh`)
        } catch (err: any) {
            toast.error("Lỗi tải ảnh lên: " + err.message)
        } finally {
            setUploading(false)
        }
    }

    const handleDeleteImage = async (albumId: string, image: GalleryImage) => {
        if (!confirm("Bạn có chắc chắn muốn xóa ảnh này khỏi thư viện?")) return

        try {
            // Delete file from backend disk
            await settingsService.deleteGalleryFile(image.url)

            const updated = albums.map((al) => {
                if (al.id === albumId) {
                    const filteredImages = al.images.filter((img) => img.id !== image.id)
                    // If deleted image was the cover, pick a new cover
                    let cover = al.cover
                    if (al.cover === image.url) {
                        cover = filteredImages.length > 0 ? filteredImages[0].url : "images/generated-1773763950437.png"
                    }
                    return {
                        ...al,
                        cover,
                        images: filteredImages,
                    }
                }
                return al
            })

            setAlbums(updated)
            handleSave(updated)
        } catch (err: any) {
            toast.error("Không thể xóa ảnh: " + err.message)
        }
    }

    const handleSetCover = (albumId: string, imageUrl: string) => {
        const updated = albums.map((al) => {
            if (al.id === albumId) {
                return { ...al, cover: imageUrl }
            }
            return al
        })
        setAlbums(updated)
        handleSave(updated)
        toast.success("Đã thay đổi ảnh bìa Album")
    }

    const handleToggleShowOnHome = (albumId: string, checked: boolean) => {
        const updated = albums.map((al) => {
            if (al.id === albumId) {
                return { ...al, showOnHome: checked }
            }
            return al
        })
        setAlbums(updated)
        handleSave(updated)
        if (checked) {
            toast.success("Album sẽ được hiển thị ở trang chủ")
        } else {
            toast.success("Đã ẩn Album khỏi trang chủ")
        }
    }


    const activeAlbum = albums.find((al) => al.id === activeAlbumId)

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-nhacam-primary" />
                <span className="ml-2 font-medium text-gray-500">Đang tải thư viện ảnh...</span>
            </div>
        )
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8 flex items-center justify-between border-b border-gray-100 pb-5">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Images className="h-6 w-6 text-nhacam-primary" /> Quản lý Thư viện ảnh
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Tạo album, tải ảnh lên và tùy chỉnh hình ảnh hiển thị ở trang thư viện công cộng.
                    </p>
                </div>
                {activeAlbumId && (
                    <Button
                        variant="outline"
                        onClick={() => setActiveAlbumId(null)}
                        className="flex items-center gap-1"
                    >
                        <ChevronLeft className="h-4 w-4" /> Quay lại danh sách
                    </Button>
                )}
            </div>

            {/* Album Detail View */}
            {activeAlbumId && activeAlbum ? (
                <div>
                    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                Album: {activeAlbum.title}
                            </h2>
                            <p className="text-xs text-gray-400 mt-1">ID: {activeAlbum.id} | {activeAlbum.images.length} hình ảnh</p>
                        </div>
                        
                        {/* File Upload Trigger */}
                        <div className="relative">
                            <input
                                type="file"
                                id="gallery-file-upload"
                                multiple
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, activeAlbum.id)}
                                className="hidden"
                                disabled={uploading}
                            />
                            <label htmlFor="gallery-file-upload">
                                <Button
                                    asChild
                                    variant="default"
                                    className="cursor-pointer flex items-center gap-1.5 bg-nhacam-primary hover:bg-nhacam-primary/90 text-white"
                                >
                                    <span>
                                        {uploading ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                        ) : (
                                            <UploadCloud className="h-4 w-4 mr-1" />
                                        )}
                                        Tải ảnh lên
                                    </span>
                                </Button>
                            </label>
                        </div>
                    </div>

                    {/* Image Grid */}
                    {activeAlbum.images.length === 0 ? (
                        <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-2xl p-12 bg-gray-50">
                            <Image className="h-12 w-12 text-gray-300 mb-3" />
                            <p className="text-sm font-medium text-gray-500">Album này chưa có ảnh nào</p>
                            <p className="text-xs text-gray-400 mt-1">Hãy nhấn nút Tải ảnh lên ở trên để bắt đầu thêm ảnh.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
                            {activeAlbum.images.map((img) => {
                                const isCover = activeAlbum.cover === img.url
                                return (
                                    <div
                                        key={img.id}
                                        className="relative aspect-square group overflow-hidden rounded-xl border border-gray-100 bg-gray-50 shadow-sm"
                                    >
                                        <img
                                            src={settingsService.getFullImageUrl(img.url)}
                                            alt=""
                                            className="h-full w-full object-cover"
                                        />
                                        
                                        {/* Cover Badge */}
                                        {isCover && (
                                            <div className="absolute top-2 left-2 rounded-md bg-nhacam-primary px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                                                Ảnh bìa
                                            </div>
                                        )}

                                        {/* Hover Overlay */}
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-center gap-2 p-2">
                                            {!isCover && (
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    className="w-full text-xs font-semibold py-1 h-7"
                                                    onClick={() => handleSetCover(activeAlbum.id, img.url)}
                                                >
                                                    Đặt làm ảnh bìa
                                                </Button>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                className="w-full text-xs font-semibold py-1 h-7 flex items-center justify-center gap-1"
                                                onClick={() => handleDeleteImage(activeAlbum.id, img)}
                                            >
                                                <Trash2 className="h-3 w-3" /> Xóa ảnh
                                            </Button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            ) : (
                /* Album Grid List View */
                <div>
                    {/* Create New Album Input Bar */}
                    <div className="mb-6 flex max-w-md items-center gap-2">
                        <Input
                            placeholder="Nhập tên Album mới..."
                            value={newAlbumTitle}
                            onChange={(e) => setNewAlbumTitle(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleCreateAlbum()
                            }}
                        />
                        <Button
                            onClick={handleCreateAlbum}
                            disabled={saving}
                            className="bg-nhacam-primary hover:bg-nhacam-primary/90 text-white flex items-center gap-1.5"
                        >
                            <FolderPlus className="h-4 w-4" /> Tạo Album
                        </Button>
                    </div>

                    {albums.length === 0 ? (
                        <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-2xl p-12 bg-gray-50">
                            <Images className="h-12 w-12 text-gray-300 mb-3" />
                            <p className="text-sm font-medium text-gray-500">Chưa có Album nào</p>
                            <p className="text-xs text-gray-400 mt-1">Nhập tên album phía trên để tạo album ảnh đầu tiên.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                            {albums.map((album) => {
                                const isEditing = editingAlbumId === album.id
                                return (
                                    <div
                                        key={album.id}
                                        onClick={() => setActiveAlbumId(album.id)}
                                        className="relative flex flex-col justify-between overflow-hidden rounded-xl border border-gray-200 bg-white hover:border-nhacam-primary/50 shadow-sm cursor-pointer group transition-all duration-200"
                                    >
                                        {/* Cover Image */}
                                        <div className="relative aspect-video w-full bg-gray-50 overflow-hidden">
                                            <img
                                                src={settingsService.getFullImageUrl(album.cover)}
                                                alt={album.title}
                                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                            />
                                            <div className="absolute top-2 right-2 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-[2px]">
                                                {album.images.length} ảnh
                                            </div>
                                        </div>

                                        {/* Card Info & Actions */}
                                        <div className="p-4">
                                            {isEditing ? (
                                                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                    <Input
                                                        size={1}
                                                        className="h-8 py-1"
                                                        value={editingTitle}
                                                        onChange={(e) => setEditingTitle(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") handleSaveRename(album.id)
                                                        }}
                                                    />
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleSaveRename(album.id)}
                                                        className="h-8 px-2"
                                                    >
                                                        Lưu
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => setEditingAlbumId(null)}
                                                        className="h-8 px-2"
                                                    >
                                                        Hủy
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col gap-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-bold text-gray-800 leading-tight block truncate pr-2">
                                                            {album.title}
                                                        </span>
                                                        
                                                        {/* Album Operations */}
                                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                            <button
                                                                onClick={(e) => handleStartRename(album, e)}
                                                                className="p-1 hover:text-nhacam-primary text-gray-400"
                                                                title="Đổi tên"
                                                            >
                                                                <Edit3 className="h-3.5 w-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={(e) => handleDeleteAlbum(album, e)}
                                                                className="p-1 hover:text-red-500 text-gray-400"
                                                                title="Xóa Album"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mt-1" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            id={`show-home-${album.id}`}
                                                            checked={!!album.showOnHome}
                                                            onChange={(e) => handleToggleShowOnHome(album.id, e.target.checked)}
                                                            className="rounded border-gray-300 text-nhacam-primary focus:ring-nhacam-primary h-3.5 w-3.5 cursor-pointer"
                                                        />
                                                        <label
                                                            htmlFor={`show-home-${album.id}`}
                                                            className="text-xs text-gray-500 font-medium cursor-pointer"
                                                        >
                                                            Hiện ở trang chủ
                                                        </label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
